/**
 * Client observability bootstrap (Sentry + PostHog).
 *
 * Both vendors are OPTIONAL. Ad blockers, DNS filters, CSP, and vendor outages
 * all make these scripts fail to load, so nothing here may ever reject or throw
 * into the page: `window.__observabilityReady` always resolves with the client
 * API, and every method degrades to a no-op when its vendor is unavailable.
 */

export function initClientObservability() {
  // Overridable so tests don't have to wait out the real timeouts.
  const timeouts = window.__OBSERVABILITY_TIMEOUTS__ || {};
  const CONFIG_FETCH_TIMEOUT_MS = timeouts.configFetchMs ?? 5000;
  const SCRIPT_LOAD_TIMEOUT_MS = timeouts.scriptLoadMs ?? 8000;
  const POSTHOG_READY_POLL_MS = timeouts.posthogPollMs ?? 100;

  /** Set only once a vendor is loaded AND initialized. Never the loader stub. */
  let sentryClient = null;
  let posthogClient = null;

  function warn(message, err) {
    try {
      console.warn(`[observability] ${message}`, err || '');
    } catch {
      // console itself can be unavailable in odd embedders
    }
  }

  /**
   * Official posthog-js loader snippet, verbatim.
   *
   * It must run before array.js: array.js only takes over a stub that the
   * snippet built (it reads `posthog._i`). Loading array.js against a bare
   * `window.posthog = []` leaves the array untouched with no `.init`, which is
   * why PostHog silently never started. The snippet also injects array.js
   * itself, from api_host with `.i.posthog.com` -> `-assets.i.posthog.com`.
   *
   * Every stub method queues instead of throwing, so calls made before (or
   * without) a successful load are harmless.
   */
  /* eslint-disable */
  /* prettier-ignore */
  function installPostHogSnippet() {
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  }
  /* eslint-enable */

  /**
   * The snippet injects its own script tag, so there is no onerror to hook.
   * `__loaded` flipping true is the only reliable signal that array.js actually
   * arrived and replaced the stub -- it stays false for ad blockers, CSP
   * refusals, empty 200s, and vendor outages alike.
   */
  function waitForPostHogLoad() {
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + SCRIPT_LOAD_TIMEOUT_MS;
      const poll = () => {
        if (window.posthog && window.posthog.__loaded) {
          resolve(window.posthog);
          return;
        }
        if (Date.now() >= deadline) {
          reject(new Error('PostHog did not finish loading'));
          return;
        }
        setTimeout(poll, POSTHOG_READY_POLL_MS);
      };
      poll();
    });
  }

  /** Resolves on load, rejects on error/timeout. Never leaves a pending listener. */
  function loadScript(src, { crossOrigin } = {}) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const script = document.createElement('script');
      const finish = (fn, arg) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        fn(arg);
      };
      const timer = setTimeout(
        () => finish(reject, new Error(`Timed out loading ${src}`)),
        SCRIPT_LOAD_TIMEOUT_MS
      );

      script.src = src;
      script.async = true;
      if (crossOrigin) script.crossOrigin = crossOrigin;
      script.onload = () => finish(resolve);
      script.onerror = () => finish(reject, new Error(`Failed to load ${src}`));

      try {
        document.head.appendChild(script);
      } catch (err) {
        finish(reject, err);
      }
    });
  }

  function buildSentryIntegrations() {
    const integrations = [];
    try {
      if (typeof window.Sentry?.browserTracingIntegration === 'function') {
        integrations.push(window.Sentry.browserTracingIntegration());
      }
      if (typeof window.Sentry?.replayIntegration === 'function') {
        integrations.push(
          window.Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          })
        );
      }
      if (typeof window.Sentry?.captureConsoleIntegration === 'function') {
        integrations.push(window.Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }));
      }
    } catch (err) {
      warn('Failed building Sentry integrations:', err);
    }
    return integrations;
  }

  async function initSentry(config) {
    if (config.sentryEnabled === false || !config.sentryDsn) return null;

    await loadScript('https://browser.sentry-cdn.com/10.63.0/bundle.tracing.replay.min.js', {
      crossOrigin: 'anonymous',
    });

    // A blocker can 200 an empty body: the script "loads" but defines nothing.
    if (typeof window.Sentry?.init !== 'function') {
      throw new Error('Sentry loaded but did not initialize');
    }

    window.Sentry.init({
      dsn: config.sentryDsn,
      environment: config.environment || 'production',
      tracesSampleRate: Number(config.tracesSampleRate ?? 0.2),
      replaysSessionSampleRate: Number(config.replaysSessionSampleRate ?? 0.1),
      replaysOnErrorSampleRate: Number(config.replaysOnErrorSampleRate ?? 1),
      integrations: buildSentryIntegrations(),
    });
    window.Sentry.setTag('app', 'signaltaxi-ai');

    sentryClient = window.Sentry;
    return sentryClient;
  }

  async function initPostHog(config) {
    if (config.posthogEnabled === false || !config.posthogKey) return null;

    installPostHogSnippet();
    if (typeof window.posthog?.init !== 'function') {
      throw new Error('PostHog snippet unavailable');
    }

    // init() is what injects array.js; it queues until the real client is live.
    window.posthog.init(config.posthogKey, {
      api_host: config.posthogHost || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: { password: true },
      },
    });

    // Deliberately left in place on failure: the stub queues harmlessly, and
    // posthogClient staying null is what keeps this module from using it.
    await waitForPostHogLoad();

    window.posthog.register({
      app: 'signaltaxi-ai',
      environment: config.environment || 'production',
    });

    posthogClient = window.posthog;
    return posthogClient;
  }

  /** Every method is a no-op unless the vendor is genuinely live. */
  function callPostHog(method, ...args) {
    if (!posthogClient || typeof posthogClient[method] !== 'function') return;
    try {
      posthogClient[method](...args);
    } catch (err) {
      warn(`PostHog ${method}() failed:`, err);
    }
  }

  const clientApi = {
    trackPage(page, extra) {
      callPostHog('capture', '$pageview', {
        page,
        $current_url: window.location.href,
        ...extra,
      });
    },
    identifyUser(user) {
      if (!user?.id) return;
      callPostHog('identify', String(user.id), {
        email: user.email || undefined,
        name: user.name || undefined,
      });
    },
    resetUser() {
      callPostHog('reset');
    },
    capture(event, properties) {
      callPostHog('capture', event, properties || {});
    },
    captureError(error) {
      if (!sentryClient || typeof sentryClient.captureException !== 'function') return;
      try {
        sentryClient.captureException(error);
      } catch (err) {
        warn('Sentry captureException() failed:', err);
      }
    },
    get isSentryActive() {
      return Boolean(sentryClient);
    },
    get isPostHogActive() {
      return Boolean(posthogClient);
    },
  };

  async function fetchConfig() {
    // AbortController guards against a proxy that accepts but never answers.
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS)
      : null;

    try {
      const response = await fetch('/api/public/observability-config', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      });
      if (!response.ok) return {};
      return (await response.json()) || {};
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  function registerGlobalErrorHandlers() {
    window.addEventListener('error', (event) => {
      const error = event.error || new Error(event.message || 'window.error');
      clientApi.captureError(error);
      clientApi.capture('client_error', {
        message: event.message || 'window.error',
        source: 'window.error',
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      clientApi.captureError(reason instanceof Error ? reason : new Error(String(reason)));
      clientApi.capture('client_unhandled_rejection', {
        message: reason instanceof Error ? reason.message : String(reason),
      });
    });
  }

  const readyPromise = (async () => {
    let config = {};
    try {
      config = await fetchConfig();
    } catch (err) {
      warn('Failed to load client config:', err);
      return clientApi;
    }

    // Vendors are independent: one failing must not skip the other.
    await Promise.all([
      initSentry(config).catch((err) => warn('Sentry init failed:', err)),
      initPostHog(config).catch((err) => warn('PostHog init failed:', err)),
    ]);

    clientApi.trackPage(document.title || 'app', { surface: 'initial_load' });

    try {
      registerGlobalErrorHandlers();
    } catch (err) {
      warn('Failed to register global error handlers:', err);
    }

    return clientApi;
  })()
    // Last line of defence: callers await this, so it must never reject.
    .catch((err) => {
      warn('Observability bootstrap failed:', err);
      return clientApi;
    });

  window.__observability = clientApi;
  window.__observabilityReady = readyPromise;

  return readyPromise;
}
